import { FC, useMemo, useRef } from 'react';
import type { InputRef } from 'antd';
import { TbCalendar, TbClock, TbFileDescription, TbFileText, TbSlash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import {
    createFilterableColumn,
    type FilterableColumn,
    type ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { WorkspaceResourceFilterControls } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/WorkspaceResourceFiltersRow';
import { SearchFilterInput } from '@/shared';
import { ListViewColumnDataType } from '@/types';

import type { SECFiling } from '../../types';
import { getFilingType } from '../../utils';

interface FilingFiltersToolbarProps {
    filings: SECFiling[];
    filters: ListFilterRule[];
    onFiltersChange: (filters: ListFilterRule[]) => void;
    onQueryChange: (query: string) => void;
    query: string;
}

const FilingFiltersToolbar: FC<FilingFiltersToolbarProps> = ({
    filings,
    filters,
    onFiltersChange,
    onQueryChange,
    query,
}) => {
    const { token } = useAntDesignTheme();
    const searchInputRef = useRef<InputRef>(null);
    const iconColor = token.colorTextTertiary;
    // The filing-local search must win over the global Search route input while this tab is visible.
    useSlashSearchFocus(searchInputRef, true, { priority: 30 });
    const filingTypeOptions = useMemo(
        () =>
            Array.from(new Set(filings.map(getFilingType).filter(Boolean)))
                .sort((left, right) => left.localeCompare(right))
                .map((filingType) => ({ label: filingType, value: filingType })),
        [filings],
    );
    const columns = useMemo<FilterableColumn[]>(
        () => [
            createFilterableColumn({
                allowMultipleValues: true,
                dataType: ListViewColumnDataType.SELECT,
                filterOperators: ['equals', 'notEquals'],
                filterOptions: filingTypeOptions,
                icon: <TbFileText style={{ color: iconColor }} />,
                key: 'filingType',
                label: 'Filing Type',
                order: 0,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.SHORT_TEXT,
                icon: <TbFileDescription style={{ color: iconColor }} />,
                key: 'title',
                label: 'Title',
                order: 1,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.DATE,
                icon: <TbCalendar style={{ color: iconColor }} />,
                key: 'filingDate',
                label: 'Filing Date',
                order: 2,
            }),
            createFilterableColumn({
                dataType: ListViewColumnDataType.DATE,
                icon: <TbClock style={{ color: iconColor }} />,
                key: 'acceptedDate',
                label: 'Accepted Date',
                order: 3,
            }),
        ],
        [filingTypeOptions, iconColor],
    );

    return (
        <div style={{ backgroundColor: token.colorBgContainer, flex: 'none' }}>
            <div
                style={{
                    borderBottom: `1px solid ${token.colorBorder}`,
                    padding: '0.5rem 0.75rem',
                }}
            >
                <SearchFilterInput
                    ref={searchInputRef}
                    ariaLabel='Search filings'
                    bordered
                    placeholder='Search Filings...'
                    size='small'
                    suffix={<TbSlash size='1rem' style={{ color: token.colorTextQuaternary, marginRight: 0 }} />}
                    value={query}
                    setValue={onQueryChange}
                />
            </div>
            <WorkspaceResourceFilterControls
                columns={columns}
                docsLabel='filing filters'
                emptyColumnsDescription='Filing filter fields are unavailable.'
                emptyHint='Filter filings by type, title, filing date, or acceptance date.'
                filterableResourceLabel='Filings'
                filters={filters}
                horizontalPadding='0.75rem'
                onChange={(nextFilters) => onFiltersChange(nextFilters ?? [])}
                resourceLabel='the filing inventory'
            />
        </div>
    );
};

export default FilingFiltersToolbar;
